import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import type {
  PreSetCategory,
  PreSetTask,
  PreSetDocument,
  PreSetFavorite,
  MyPreSet,
  PreSetSelection,
} from '../types/preset.types';
import * as preSetApi from '../api/preSetApi';
import { getCurrentJurisdiction } from '../../../jurisdictionResolver';

export const usePreSetStore = defineStore('preSet', () => {
  // ─── State ──────────────────────────────────────────────────
  const categories = ref<PreSetCategory[]>([]);
  const allTasks = ref<PreSetTask[]>([]);
  const favorites = ref<PreSetFavorite[]>([]);
  const myPreSets = ref<MyPreSet[]>([]);
  const loading = ref(false);

  // ─── My PreSet (클라이언트별 커스텀 프리셋) ──────────────────
  const myCategories = ref<PreSetCategory[]>([]);
  const myAllTasks = ref<PreSetTask[]>([]);
  const myLoading = ref(false);

  // 선택된 프리셋 (모달에서 확정 후)
  const selectedPreSet = ref<PreSetSelection | null>(null);

  // 전역 PreSet 모달 상태 (어느 메뉴에서든 Start Task 클릭 시 모달 노출)
  const isModalOpen = ref(false);

  // ─── Computed ───────────────────────────────────────────────

  /** 카테고리 트리 (Level 1 > Level 2) */
  const categoryTree = computed(() => {
    const level1 = categories.value.filter(c => c.CATEGORY_LEVEL === 1);
    return level1.map(parent => ({
      ...parent,
      children: categories.value.filter(c => c.PARENT_ID === parent.CATEGORY_ID && c.CATEGORY_LEVEL === 2),
    }));
  });

  /** 즐겨찾기 PRESET_TASK_ID Set (빠른 조회용) */
  const favoriteIds = computed(() => new Set(favorites.value.map(f => f.PRESET_TASK_ID)));

  /** My PreSet 카테고리 트리 (Level 1 > Level 2) */
  const myCategoryTree = computed(() => {
    const level1 = myCategories.value.filter(c => c.CATEGORY_LEVEL === 1);
    return level1.map(parent => ({
      ...parent,
      children: myCategories.value.filter(c => c.PARENT_ID === parent.CATEGORY_ID && c.CATEGORY_LEVEL === 2),
    }));
  });

  // ─── Actions ────────────────────────────────────────────────

  /**
   * 초기 데이터 로드 (카테고리 + 전체 태스크 + 즐겨찾기).
   * countryCd 미지정 시 현재 세션 jurisdiction 으로 자동 결정 ('MY' / 'SG').
   * 알 수 없는 jurisdiction 이면 'MY' fallback (back-compat).
   */
  async function loadAll(countryCd?: string) {
    loading.value = true;
    const resolvedCd = countryCd || getCurrentJurisdiction() || 'MY';
    try {
      const [cats, tasks, favs] = await Promise.all([
        preSetApi.getCategories(resolvedCd).catch((err) => { console.error('[PreSetStore] getCategories failed:', err); return []; }),
        preSetApi.getAllTasks(resolvedCd).catch((err) => { console.error('[PreSetStore] getAllTasks failed:', err); return []; }),
        preSetApi.getFavorites().catch((err) => { console.error('[PreSetStore] getFavorites failed:', err); return []; }),
      ]);
      console.log(`[PreSetStore] loadAll(${resolvedCd}) result:`, { cats: cats?.length, tasks: tasks?.length, favs: favs?.length });
      categories.value = cats;
      allTasks.value = tasks;
      favorites.value = favs;
    } catch (err) {
      console.error('[PreSetStore] loadAll failed:', err);
    } finally {
      loading.value = false;
    }
  }

  /** 카테고리(Menu)별 태스크 필터 */
  function getTasksByMenu(menuCategoryId: number): PreSetTask[] {
    return allTasks.value.filter(t => t.CATEGORY_ID === menuCategoryId);
  }

  /**
   * My PreSet 데이터 로드 (로그인 클라이언트 전용 카테고리 + 태스크).
   * 세션 없으면 빈 배열 (백엔드에서 graceful).
   */
  async function loadMyAll(countryCd?: string) {
    myLoading.value = true;
    const resolvedCd = countryCd || getCurrentJurisdiction() || 'MY';
    try {
      const [cats, tasks] = await Promise.all([
        preSetApi.getMyCategories(resolvedCd).catch((err) => { console.error('[PreSetStore] getMyCategories failed:', err); return []; }),
        preSetApi.getMyAllTasks(resolvedCd).catch((err) => { console.error('[PreSetStore] getMyAllTasks failed:', err); return []; }),
      ]);
      console.log(`[PreSetStore] loadMyAll(${resolvedCd}) result:`, { cats: cats?.length, tasks: tasks?.length });
      myCategories.value = cats;
      myAllTasks.value = tasks;
    } catch (err) {
      console.error('[PreSetStore] loadMyAll failed:', err);
    } finally {
      myLoading.value = false;
    }
  }

  /** My PreSet — 카테고리(Menu)별 태스크 필터 */
  function getMyTasksByMenu(menuCategoryId: number): PreSetTask[] {
    return myAllTasks.value.filter(t => t.CATEGORY_ID === menuCategoryId);
  }

  /** My PreSet 태스크의 문서 목록 조회 */
  async function fetchMyTaskDocuments(presetTaskId: number): Promise<PreSetDocument[]> {
    return preSetApi.getMyTaskDocuments(presetTaskId);
  }

  /** 태스크의 문서 목록 조회 */
  async function fetchTaskDocuments(presetTaskId: number): Promise<PreSetDocument[]> {
    return preSetApi.getTaskDocuments(presetTaskId);
  }

  /** 클라이언트별 문서 순서 저장 (드래그앤드롭) */
  async function saveTaskDocOrder(presetTaskId: number, order: string[]): Promise<void> {
    await preSetApi.saveTaskDocOrder(presetTaskId, order);
  }

  /** My Preset 문서 순서 저장 (드래그앤드롭) */
  async function saveMyTaskDocOrder(presetTaskId: number, order: string[]): Promise<void> {
    await preSetApi.saveMyTaskDocOrder(presetTaskId, order);
  }

  /** 즐겨찾기 토글 */
  async function toggleFavorite(presetTaskId: number) {
    if (favoriteIds.value.has(presetTaskId)) {
      await preSetApi.removeFavorite(presetTaskId);
      favorites.value = favorites.value.filter(f => f.PRESET_TASK_ID !== presetTaskId);
    } else {
      await preSetApi.addFavorite(presetTaskId);
      // 로컬에 추가 (태스크 정보 포함)
      const task = allTasks.value.find(t => t.PRESET_TASK_ID === presetTaskId);
      if (task) {
        favorites.value.push({
          FAVORITE_ID: 0, // 서버에서 생성된 ID는 다음 조회 시 갱신
          PRESET_TASK_ID: presetTaskId,
          TASK_NAME: task.TASK_NAME,
          TASK_CODE: task.TASK_CODE,
          DOC_CODES: task.DOC_CODES,
          CATEGORY_ID: task.CATEGORY_ID,
        });
      }
    }
  }

  /** MyPreSet 목록 로드 */
  async function loadMyPreSets() {
    myPreSets.value = await preSetApi.getMyPreSets();
  }

  /** MyPreSet 저장 */
  async function saveMyPreSet(presetName: string, presetData: any) {
    const id = await preSetApi.createMyPreSet(presetName, presetData);
    await loadMyPreSets();
    return id;
  }

  /** MyPreSet 수정 */
  async function updateMyPreSet(id: number, presetName: string, presetData: any) {
    await preSetApi.updateMyPreSet(id, presetName, presetData);
    await loadMyPreSets();
  }

  /** MyPreSet 삭제 */
  async function deleteMyPreSet(id: number) {
    await preSetApi.deleteMyPreSet(id);
    myPreSets.value = myPreSets.value.filter(p => p.MY_PRESET_ID !== id);
  }

  /** PreSet 사용 로그 기록 */
  async function logUse(presetTaskId: number, taskId: number) {
    await preSetApi.logPreSetUse(presetTaskId, taskId);
  }

  /** 선택 확정 */
  function confirmSelection(selection: PreSetSelection) {
    selectedPreSet.value = selection;
  }

  /** 선택 초기화 */
  function clearSelection() {
    selectedPreSet.value = null;
  }

  /** 전역 PreSet 모달 열기 (어느 메뉴에서든 호출 가능) */
  function openModal() {
    isModalOpen.value = true;
  }

  /** 전역 PreSet 모달 닫기 */
  function closeModal() {
    isModalOpen.value = false;
  }

  return {
    // state
    categories,
    allTasks,
    favorites,
    myPreSets,
    loading,
    myCategories,
    myAllTasks,
    myLoading,
    selectedPreSet,
    isModalOpen,
    // computed
    categoryTree,
    favoriteIds,
    myCategoryTree,
    // actions
    loadAll,
    saveTaskDocOrder,
    saveMyTaskDocOrder,
    getTasksByMenu,
    fetchTaskDocuments,
    loadMyAll,
    getMyTasksByMenu,
    fetchMyTaskDocuments,
    toggleFavorite,
    loadMyPreSets,
    saveMyPreSet,
    updateMyPreSet,
    deleteMyPreSet,
    logUse,
    confirmSelection,
    clearSelection,
    openModal,
    closeModal,
  };
});
